import React from 'react';
import styled from 'styled-components';
import { color, screen } from 'theme';
import HtmlTrans from 'components/shared/HtmlTrans';
import { Icon } from '@asgard/ui-library';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';

interface Fredcon26SpeakerProps {
  borderColor: string;
  speakerPhoto: string;
  name: string;
  surname: string;
  descriptionKey: string;
  linkedinUrl?: string;
}

const Wrapper = styled.div<{ borderColor: string }>`
  background-color: ${color('white')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: clip;
  position: relative;
  width: 240px;
  border: none;
  outline: none;

  @media (max-width: ${screen('md')}) {
    flex-direction: row;
    align-items: center;
    width: 100%;
    max-width: clamp(400px, 80vw, 540px);
    margin: 0 auto;
  }
`;

const PhotoContainer = styled.div`
  position: relative;
  height: 240px;
  width: 240px;
  background-color: ${color('white')};
  flex-shrink: 0;
  border: none;
  outline: none;

  .scaled-photo-container {
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    height: 90%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    z-index: 0;
  }

  .photo-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .speaker-photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${screen('md')}) {
    width: clamp(127px, 35vw, 240px);
    height: auto;
    aspect-ratio: 1 / 1;
  }
`;

const PixelsOverlay = styled.div<{ color: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ color }) => color};
  mask: url('/images/fredcon26/pixels.svg') no-repeat center;
  mask-size: cover;
  z-index: 1;
  pointer-events: none;
`;

const TextContent = styled.div`
  background-color: ${color('white')};
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px 14px;
  width: 100%;
  text-transform: uppercase;
  z-index: 2;

  @media (max-width: ${screen('md')}) {
    padding: clamp(16px, 2.9vw, 20px);
    height: auto;
    flex: 1;
    justify-content: center;
  }
`;

const NameWrapper = styled.div`
  display: flex;
  gap: 4px;
  font-family: 'Archivo Narrow', sans-serif;
  font-weight: 600;
  font-size: 28px;
  white-space: nowrap;
  align-items: center;

  .name {
    color: ${color('black')};
  }

  .surname {
    color: #3053f9;
  }

  @media (max-width: ${screen('md')}) {
    font-size: clamp(20px, 4.1vw, 28px);
    line-height: 1.1;
  }
`;

const Description = styled.p`
  font-family: 'Archivo Narrow', sans-serif;
  font-weight: 400;
  font-size: 20px;
  color: ${color('black')};
  line-height: 1.2;
  margin: 0;

  @media (max-width: ${screen('md')}) {
    font-size: 19px;
    line-height: 1.2;
  }
`;

const LinkedinLink = styled.a`
  color: #3053f9;
  display: flex;
  align-items: center;
  margin-top: 8px;
  transition: opacity 0.2s ease;
  width: 24px;
  height: 24px;
  overflow: visible;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 24px !important;
    height: 24px !important;
    pointer-events: none;
  }
`;

export const Fredcon26Speaker: React.FC<Fredcon26SpeakerProps> = ({
  borderColor,
  speakerPhoto,
  name,
  surname,
  descriptionKey,
  linkedinUrl
}) => {
  return (
    <Wrapper borderColor={borderColor}>
      <PhotoContainer>
        <div className="scaled-photo-container">
          <div className="photo-wrapper">
            <img
              src={speakerPhoto}
              alt={`${name} ${surname}`}
              className="speaker-photo"
            />
          </div>
        </div>
        <PixelsOverlay color={borderColor} />
      </PhotoContainer>

      <TextContent>
        <NameWrapper>
          <span className="name">{name}</span>
          <span className="surname">{surname}</span>
        </NameWrapper>

        <Description>
          <HtmlTrans ns="fredcon26" i18nKey={descriptionKey} />
        </Description>

        {linkedinUrl && (
          <LinkedinLink
            href={linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon icon={faLinkedin} />
          </LinkedinLink>
        )}
      </TextContent>
    </Wrapper>
  );
};
